import { getActionError } from "../utils/actionFeedback";
import { toast } from "react-toastify";
import { Fragment, useRef, useState } from "react";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Description,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { FaCalendarAlt, FaCheck, FaTimes } from "react-icons/fa";
import axiosInstance from "../Context/axiosInstance";

export default function RequestSessionModal({ open, onClose, isDarkMode }) {
  const [whatsapp, setWhatsapp] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);
  const submitting = useRef(false);
  const close = () => {
    if (!submitting.current) onClose();
  };
  const muted = isDarkMode ? "text-[var(--study-muted)]" : "text-slate-500";
  const input =
    "mt-2 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400 disabled:opacity-60";

  const submit = async (event) => {
    event.preventDefault();
    if (submitting.current || success) return;
    const number = whatsapp.trim();
    const details = description.trim();
    if (!number) return setError("WhatsApp number cannot be empty.");
    if (!/^\+?[0-9]\d{1,14}$/.test(number))
      return setError(
        "Enter a valid WhatsApp number, including your country code.",
      );
    if (details.length < 6)
      return setError("Description must be at least 6 characters long.");
    submitting.current = true;
    setBusy(true);
    setError("");
    try {
      await axiosInstance.post("/sessions/requests", {
        whatsAppNumber: number,
        description: details,
      });
      toast.success("Session request sent successfully.");
      setSuccess(true);
    } catch (err) {
      toast.error(getActionError(err, "Could not send the session request."));
      const data = err.response?.data;
      const reasons = Array.isArray(data?.errors)
        ? data.errors
            .map((item) => item.reason)
            .filter((reason) => typeof reason === "string")
            .join(" ")
        : "";
      const message = reasons || data?.detail || data?.message;
      setError(
        typeof message === "string"
          ? message
          : "Your request couldn't be sent. Please try again.",
      );
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  };

  return (
    <Transition
      appear
      show={open}
      as={Fragment}
      afterLeave={() => {
        if (success) {
          setWhatsapp("");
          setDescription("");
        }
        setSuccess(false);
        setError("");
      }}
    >
      <Dialog onClose={close} className="relative z-[100]">
        <TransitionChild
          as={Fragment}
          enter="transition-opacity duration-200 motion-reduce:duration-0"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity duration-150 motion-reduce:duration-0"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm"
            aria-hidden="true"
          />
        </TransitionChild>
        <div className="fixed inset-0 overflow-y-auto p-4 sm:p-6">
          <div className="flex min-h-full items-center justify-center">
            <TransitionChild
              as={Fragment}
              enter="transition duration-200 ease-out motion-reduce:duration-0"
              enterFrom="translate-y-4 scale-95 opacity-0 motion-reduce:transform-none"
              enterTo="translate-y-0 scale-100 opacity-100"
              leave="transition duration-150 ease-in motion-reduce:duration-0"
              leaveFrom="translate-y-0 scale-100 opacity-100"
              leaveTo="translate-y-2 scale-95 opacity-0 motion-reduce:transform-none"
            >
              <DialogPanel
                className={`relative flex h-[590px] max-h-[calc(100dvh-3rem)] w-full max-w-lg flex-col overflow-hidden rounded-3xl border shadow-2xl ${isDarkMode ? "border-[var(--study-border)] bg-[var(--study-surface)] text-[var(--study-text)]" : "border-slate-200 bg-white text-slate-900"}`}
              >
                <div className="shrink-0 px-6 pt-6 sm:px-8 sm:pt-8">
                  <button
                    type="button"
                    aria-label="Close date request"
                    disabled={busy}
                    onClick={close}
                    className={`absolute right-4 top-4 rounded-full p-3 focus-visible:ring-2 focus-visible:ring-slate-400 disabled:opacity-40 ${muted}`}
                  >
                    <FaTimes aria-hidden="true" />
                  </button>
                  <span
                    className={`mb-4 flex h-11 w-11 items-center justify-center rounded-xl ${success ? "bg-emerald-100 text-emerald-700" : isDarkMode ? "bg-white/5 text-red-300" : "bg-slate-100 text-slate-700"}`}
                  >
                    {success ? (
                      <FaCheck aria-hidden="true" />
                    ) : (
                      <FaCalendarAlt aria-hidden="true" />
                    )}
                  </span>
                  <DialogTitle className="text-2xl font-bold">
                    {success ? "Request sent" : "Request a date"}
                  </DialogTitle>
                  <Description
                    className={`mt-2 text-sm leading-relaxed ${muted}`}
                  >
                    {success
                      ? "Your session request has been sent to the team."
                      : "Tell us when you'd like a session and what you'd like to cover."}
                  </Description>
                </div>
                <div
                  className="min-h-0 flex-1 overflow-y-auto px-6 py-5 sm:px-8"
                  aria-busy={busy}
                >
                  {success ? (
                    <div
                      role="status"
                      className="flex h-full flex-col items-center justify-center text-center"
                    >
                      <FaCheck
                        aria-hidden="true"
                        className="mb-4 text-4xl text-emerald-500"
                      />
                      <p className="font-semibold">
                        Thanks for sharing your preferred date.
                      </p>
                      <p className={`mt-2 text-sm ${muted}`}>
                        The team will review your request. This is not a
                        confirmed reservation yet.
                      </p>
                    </div>
                  ) : (
                    <form
                      id="request-session-form"
                      onSubmit={submit}
                      noValidate
                      className="space-y-5"
                    >
                      <label
                        className="block text-sm font-semibold"
                        htmlFor="session-request-whatsapp"
                      >
                        WhatsApp number
                        <input
                          id="session-request-whatsapp"
                          type="tel"
                          autoComplete="tel"
                          placeholder="Include your country code"
                          value={whatsapp}
                          disabled={busy}
                          onChange={(event) => setWhatsapp(event.target.value)}
                          className={input}
                        />
                      </label>
                      <label
                        className="block text-sm font-semibold"
                        htmlFor="session-request-description"
                      >
                        Session details
                        <textarea
                          id="session-request-description"
                          rows={4}
                          placeholder="Your preferred date, time, and the topics you want to discuss"
                          value={description}
                          disabled={busy}
                          onChange={(event) =>
                            setDescription(event.target.value)
                          }
                          className={`${input} resize-none`}
                        />
                      </label>
                      {error && (
                        <p
                          role="alert"
                          className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700"
                        >
                          {error}
                        </p>
                      )}
                    </form>
                  )}
                </div>
                <div
                  className={`flex shrink-0 justify-end gap-3 border-t px-6 py-4 sm:px-8 ${isDarkMode ? "border-[var(--study-border)]" : "border-slate-200"}`}
                >
                  {!success && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={close}
                      className={`min-h-[44px] rounded-xl px-5 text-sm font-semibold focus-visible:ring-2 focus-visible:ring-slate-400 disabled:opacity-40 ${muted}`}
                    >
                      Cancel
                    </button>
                  )}
                  {success ? (
                    <button
                      type="button"
                      onClick={close}
                      className="min-h-[44px] rounded-xl bg-red-700 px-6 text-sm font-semibold text-white hover:bg-red-800"
                    >
                      Done
                    </button>
                  ) : (
                    <button
                      type="submit"
                      form="request-session-form"
                      disabled={busy}
                      className={`inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl px-6 text-sm font-semibold text-white focus-visible:ring-2 focus-visible:ring-slate-400 disabled:opacity-60 ${isDarkMode ? "bg-red-700 hover:bg-red-800" : "bg-[#06315B] hover:bg-[#0b477f]"}`}
                    >
                      {busy && (
                        <span
                          aria-hidden="true"
                          className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white motion-reduce:animate-none"
                        />
                      )}
                      {busy ? "Sending..." : "Send request"}
                    </button>
                  )}
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
